import React, { useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Stack } from '@bingads-webui-react/fabric-control';
import { useFormService } from '@bingads-webui-react/form';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ReactTemplate } from '@bingads-webui-react/react-template';
import { GeneratedUI } from '@bingads-webui-campaign-react/ui-generator';
import { summaryPageComponentTypes } from '@bingads-webui-campaign-react/summary-page';
import { useWizardService, WIZARD_EVENTS } from '@bingads-webui-react/wizard';
import { FormErrorGroup, UnmappedFormErrors } from '@bingads-webui-campaign-react/form-error';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { getStyles } from './styles';
import { buildSchema } from './schema';
import { useSummarySchemaData } from './use-summary-schema-data';
import { SEARCH_CAMPAIGN_CREATION_WIZARD_STEP_PATHS } from '../step-utils';
import { StepHeader } from '../../shared/step-header';

const SummaryStepInternal = ({ data, error }) => {
  const i18n = useContext(I18nContext);
  const schemaData = useSummarySchemaData({ data });
  const { send } = useWizardService();
  const classes = useDefaultStyles(getStyles);
  const activity = useContext(ActivityContext);
  const {
    serverErrors = [],
  } = error || {};
  const { state: { context: { saveFailed: showAllValidationErrors } } } = useFormService();

  const onCampaignSettingsHeaderActionClick = useCallback(() => {
    if (activity) {
      activity.signal('onCampaignSettingsHeaderActionClick');
    }
    const stepId = SEARCH_CAMPAIGN_CREATION_WIZARD_STEP_PATHS.CAMPAIGN_SETTINGS;
    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { focusedStepId: stepId });
    send(WIZARD_EVENTS.NAVIGATE_TO, {
      stepId,
    });
  }, [send, activity]);

  const onAdsHeaderActionClick = useCallback(() => {
    if (activity) {
      activity.signal('onAdsHeaderActionClick');
    }
    const stepId = SEARCH_CAMPAIGN_CREATION_WIZARD_STEP_PATHS.CREATE_AD;
    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { focusedStepId: stepId });
    send(WIZARD_EVENTS.NAVIGATE_TO, {
      stepId,
    });
  }, [send, activity]);

  const onKeywordsHeaderActionClick = useCallback(() => {
    if (activity) {
      activity.signal('onKeywordsHeaderActionClick');
    }
    const stepId = SEARCH_CAMPAIGN_CREATION_WIZARD_STEP_PATHS.KEYWORDS;
    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { focusedStepId: stepId });
    send(WIZARD_EVENTS.NAVIGATE_TO, {
      stepId,
    });
  }, [send, activity]);

  const onBudgetBidHeaderActionClick = useCallback(() => {
    if (activity) {
      activity.signal('onBudgetBidHeaderActionClick');
    }
    const stepId = SEARCH_CAMPAIGN_CREATION_WIZARD_STEP_PATHS.BUDGET_AND_BIDS;
    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { focusedStepId: stepId });
    send(WIZARD_EVENTS.NAVIGATE_TO, {
      stepId,
    });
  }, [send, activity]);

  const options = useMemo(() => ({
    onCampaignSettingsHeaderActionClick,
    onAdsHeaderActionClick,
    onKeywordsHeaderActionClick,
    onBudgetBidHeaderActionClick,
  }), [onCampaignSettingsHeaderActionClick, onAdsHeaderActionClick, onKeywordsHeaderActionClick, onBudgetBidHeaderActionClick]);

  const schema = useMemo(() => buildSchema(options), [options]);
  const model = useMemo(() => ({ b: text => <b>{text}</b> }), []);
  return (
    <div className={classNames(classes.summaryStep, 'summary-step')}>
      <Stack tokens={{ childrenGap: 22 }}>
        <Stack>
          <StepHeader
            title={_TL_('Summary')}
          />
          <div className="summary-description">
            <ReactTemplate
              model={model}
              template={i18n.getString(_TL_("Review your Search campaign details and select {{b}}Go live{{/b}} when you're ready."))}
            />
          </div>
        </Stack>
        {data ?
          <div className="summary-sections">
            <GeneratedUI schema={schema} customComponentTypes={summaryPageComponentTypes} {...schemaData} />
          </div>
          : null
        }
        <FormErrorGroup formErrorProps={{ errors: serverErrors, showAllValidationErrors }}>
          <div className="server-errors">
            <UnmappedFormErrors />
          </div>
        </FormErrorGroup>
      </Stack>
    </div>
  );
};

export const SummaryStep = ({ data, error }) => (
  <SummaryStepInternal data={data} error={error} />
);
